import { inject, Service } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { calculateMutations } from '@swissfex/sfx-commons';
import { isValid } from 'date-fns';
import { Operation } from 'fast-json-patch';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { ROUTE_ID } from '../../constants/api-routes';
import { HttpService } from '../../core/services/http.service';
import { SESSION_STORAGE_KEY, SessionStorageService } from '../../core/services/session-storage.service';
import { ProjectUtils } from '../../core/utils/project.utils';
import { isDateToday } from '../../core/utils/utils';
import { ProductType } from '../models/enums/productType';
import { IExtendedFinancingFormGroup } from '../models/forms/IExtendedFinancingFormGroup';
import { ICachedClosestPayoutDate } from '../models/ICachedClosestPayoutDate';
import { IExtendedFinancing } from '../models/IExtendedFinancing';
import { IFinancing } from '../models/IFinancing';
import { IInstitution } from '../models/IInstitution';
import { IInstitutionsData } from '../models/IInstitutionsData';
import { ProjectsService } from './projects.service';

interface IExtendedFinancingUpdatedExternallyPayload {
    extendedFinancing: IExtendedFinancing;
    formModel: IExtendedFinancing;
}

@Service()
export class FinancingService {
    private readonly httpSvc = inject(HttpService);
    private readonly projectSvc = inject(ProjectsService);
    private readonly sessionStorageSvc = inject(SessionStorageService);

    public cachedExtendedFinancingsObservable: Observable<IExtendedFinancing>;
    public extendedFinancingFormGroupObservable: Observable<FormGroup<IExtendedFinancingFormGroup> | null>;
    // Emitted when extended financing data is updated outside of regular form updates.
    public extendedFinancingUpdatedExternallyObservable: Observable<IExtendedFinancingUpdatedExternallyPayload>;

    private cachedExtendedFinancingsSubject = new BehaviorSubject<IExtendedFinancing>(undefined);
    private extendedFinancingFormGroupSubject = new BehaviorSubject<FormGroup<IExtendedFinancingFormGroup> | null>(
        null
    );
    private withdrawalInstitutionsDataSubject = new BehaviorSubject<IInstitutionsData>(undefined);
    private extendedFinancingUpdatedExternallySubject = new Subject<IExtendedFinancingUpdatedExternallyPayload>();

    constructor() {
        this.cachedExtendedFinancingsObservable = this.cachedExtendedFinancingsSubject.asObservable();
        this.extendedFinancingFormGroupObservable = this.extendedFinancingFormGroupSubject.asObservable();
        this.extendedFinancingUpdatedExternallyObservable =
            this.extendedFinancingUpdatedExternallySubject.asObservable();
    }

    public clearCache(): void {
        this.withdrawalInstitutionsDataSubject.next(undefined);
        this.notifyObservers(undefined);
    }

    public saveExtendedFinancingFormGroupToCache(formGroup: FormGroup<IExtendedFinancingFormGroup> | null) {
        this.extendedFinancingFormGroupSubject.next(formGroup);
    }

    public async getCachedExtendedFinancingForProject(
        projectId: number,
        fetchIfNotAvailable = true,
        cancellationContext?: object
    ): Promise<IExtendedFinancing> {
        const extendedFinancing = this.cachedExtendedFinancingsSubject.getValue();
        if (extendedFinancing) {
            return Promise.resolve(extendedFinancing);
        } else if (fetchIfNotAvailable) {
            return await this.getExtendedFinancing(projectId, undefined, cancellationContext);
        }

        return undefined;
    }

    public async getClosestPayoutDate(referenceDate?: Date): Promise<Date> {
        const cachedClosestPayoutDate = this.sessionStorageSvc.get<ICachedClosestPayoutDate>(
            SESSION_STORAGE_KEY.CLOSEST_PAYOUT_DATE
        );

        if (cachedClosestPayoutDate && isDateToday(cachedClosestPayoutDate.fetchedOn) && cachedClosestPayoutDate.date) {
            try {
                const date = new Date(cachedClosestPayoutDate.date);
                if (isValid(date)) {
                    return date;
                } else {
                    this.removeClosestPayoutDateFromSessionStorage();
                }
            } catch {
                this.removeClosestPayoutDateFromSessionStorage();
            }
        }

        const { payoutDate } = await this.httpSvc.post<{ payoutDate: string }>(ROUTE_ID.CLOSEST_PAYOUT_DATE, {
            referenceDate
        });

        const dataForCaching: ICachedClosestPayoutDate = {
            date: payoutDate,
            fetchedOn: new Date().toISOString()
        };
        this.sessionStorageSvc.set(SESSION_STORAGE_KEY.CLOSEST_PAYOUT_DATE, dataForCaching);

        return new Date(payoutDate);
    }

    public async getExtendedFinancing(
        projectId: number,
        formGroup?: FormGroup,
        cancellationContext?: object
    ): Promise<IExtendedFinancing> {
        const result = await this.httpSvc.get<IExtendedFinancing>(
            formGroup,
            ROUTE_ID.FINANCINGS_FOR_PROJECT_EXTENDED,
            { projectId },
            null,
            null,
            cancellationContext
        );

        this.mapAndPerformUpdates(projectId, result);

        return result;
    }

    public async updateFinancing(projectId: number, mutations: Operation[], formGroup: FormGroup): Promise<IFinancing> {
        const actionBeforeOfferTriggerNotification = async () => {
            await this.getExtendedFinancing(projectId, formGroup);
        };

        return await this.httpSvc.jsonPatch<IFinancing>(
            formGroup,
            ROUTE_ID.FINANCINGS_FOR_PROJECT,
            { projectId },
            null,
            mutations,
            null,
            actionBeforeOfferTriggerNotification
        );
    }

    public updateFinancingForCachedProject(projectId: number, extendedFinancing: IExtendedFinancing): void {
        const project = this.projectSvc.getCachedProject(projectId, true);
        if (project) {
            project.financingsData = this.mapExtendedToShortFinancing(projectId, extendedFinancing);
            this.projectSvc.updateProjectInCache(project.id, project);
        }
    }

    public async updateExtendedFinancing(
        projectId: number,
        mutations: Operation[],
        formGroup: FormGroup,
        doNotifyObservers = true
    ): Promise<IExtendedFinancing> {
        const result = await this.httpSvc.jsonPatch<IExtendedFinancing>(
            formGroup,
            ROUTE_ID.FINANCINGS_FOR_PROJECT_EXTENDED,
            { projectId },
            null,
            mutations
        );
        if (doNotifyObservers) {
            this.notifyObservers(result);
        }

        return result;
    }

    public async createBorrower(projectId: number): Promise<IExtendedFinancing> {
        return await this.httpSvc.post<IExtendedFinancing>(ROUTE_ID.CREATE_BORROWER_FROM_FINANCING, null, {
            projectId
        });
    }

    public async getWithdrawalInstitutionsData(projectId: number): Promise<IInstitutionsData> {
        const currentInstitutionsData = this.withdrawalInstitutionsDataSubject.getValue();
        if (currentInstitutionsData) {
            return currentInstitutionsData;
        }

        const institutions = await this.httpSvc.get<IInstitution[]>(null, ROUTE_ID.WITHDRAWALS_INSTITUTIONS, {
            projectId
        });
        const institutionsData = ProjectUtils.getInstitutionsData(institutions);
        this.withdrawalInstitutionsDataSubject.next(institutionsData);

        return institutionsData;
    }

    public async updateTrancheData(
        projectId: number,
        payload: { productType: ProductType; runtime: number }
    ): Promise<IExtendedFinancing> {
        const extendedFinancing = await this.httpSvc.post<IExtendedFinancing>(
            ROUTE_ID.TRANCHE_UPDATE,
            { projectId },
            payload
        );
        this.mapAndPerformUpdates(projectId, extendedFinancing);
        return extendedFinancing;
    }

    public mapExtendedToShortFinancing(projectId: number, financing: IExtendedFinancing): IFinancing {
        if (!financing) {
            return null;
        }

        return {
            projectId,
            dateOfPayment: financing.calculated.dateOfPayment,
            numberOfTranches: financing.tranches.length,
            isFinancingExtended: financing.isFinancingExtended,
            mortgageValue: financing.mortgageValue,
            ownFundsTotal: financing.calculated.ownFundsTotal,
            collateralsTotal: financing.calculated.collateralsTotal,
            requestedAmount: financing.calculated.requestedAmount
        } as IFinancing;
    }

    public notifyExtendedFinancingExternalUpdate(
        extendedFinancing: IExtendedFinancing,
        formModel: IExtendedFinancing
    ): void {
        this.extendedFinancingUpdatedExternallySubject.next({ extendedFinancing, formModel });
    }

    public removeClosestPayoutDateFromSessionStorage(): void {
        this.sessionStorageSvc.remove(SESSION_STORAGE_KEY.CLOSEST_PAYOUT_DATE);
    }

    public notifyObservers(extendedFinancing: IExtendedFinancing) {
        if (!calculateMutations(this.cachedExtendedFinancingsSubject.getValue(), extendedFinancing).length) {
            return;
        }

        this.cachedExtendedFinancingsSubject.next(extendedFinancing);
    }

    private mapAndPerformUpdates(projectId: number, extendedFinancing: IExtendedFinancing): void {
        if (extendedFinancing && !!this.projectSvc.getCachedProject(projectId)) {
            this.updateFinancingForCachedProject(projectId, extendedFinancing);
        }

        this.notifyObservers(extendedFinancing);
    }
}
